import {
  type ColumnDef,
  type ColumnSort,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, LoaderCircle } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { cn } from '../../lib/utils';
import { ProblemBanner } from '../feedback/problem-banner';
import { EmptyState } from '../layout/empty-state';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

export type { ColumnDef, ColumnSort, RowSelectionState, SortingState };

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  /** Rows; `undefined` while the first page loads. */
  data: T[] | undefined;
  getRowId?: (row: T, index: number) => string;
  /** First load in progress (rows unknown). */
  isLoading?: boolean;
  /** Error of the first load; shows a banner instead of rows. */
  error?: unknown;
  onRetry?: () => void;

  /** Sorting. Controlled when `sorting` is given; `manualSorting` leaves ordering to the server. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  enableSorting?: boolean;

  /** Row selection (adds a checkbox column when `enableRowSelection` is set). */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean | ((row: Row<T>) => boolean);

  /** Keyset paging: show "Load more" while `hasMore`. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  loadMoreLabel?: string;

  onRowClick?: (row: T) => void;
  /** Rendered when there are zero rows and no error. */
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  /** Custom empty element (overrides the three props above). */
  empty?: ReactNode;

  caption?: string;
  dense?: boolean;
  className?: string;
  /** Slot above the table (search box, filters). */
  toolbar?: ReactNode;
  /** Slot under the table, left of "Load more" (e.g. "12 of 40 selected"). */
  footer?: ReactNode;
  /** Skeleton rows while loading. */
  skeletonRows?: number;
}

/**
 * Table over `@tanstack/react-table` with sorting, optional selection, keyset "load more" and
 * loading/empty/error states. Feed it a `usePagedList()` result via `pagedTableProps()`.
 */
export function DataTable<T>({
  columns,
  data,
  getRowId,
  isLoading = false,
  error,
  onRetry,
  sorting,
  onSortingChange,
  manualSorting = false,
  enableSorting = true,
  rowSelection,
  onRowSelectionChange,
  enableRowSelection = false,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  loadMoreLabel = 'Load more',
  onRowClick,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyAction,
  empty,
  caption,
  dense = false,
  className,
  toolbar,
  footer,
  skeletonRows = 5,
}: DataTableProps<T>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalSelection, setInternalSelection] = useState<RowSelectionState>({});
  const sortingState = sorting ?? internalSorting;
  const selectionState = rowSelection ?? internalSelection;

  const allColumns = enableRowSelection !== false ? [selectionColumn<T>(), ...columns] : columns;

  const table = useReactTable<T>({
    data: data ?? EMPTY,
    columns: allColumns,
    getRowId,
    state: { sorting: sortingState, rowSelection: selectionState },
    onSortingChange: onSortingChange ?? setInternalSorting,
    onRowSelectionChange: onRowSelectionChange ?? setInternalSelection,
    enableSorting,
    enableRowSelection,
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
  });

  const rows = table.getRowModel().rows;
  const showEmpty = !isLoading && !error && data !== undefined && rows.length === 0;
  const cellPad = dense ? 'py-1.5' : undefined;

  return (
    <div className={cn('space-y-3', className)} data-slot="data-table">
      {toolbar}
      {error ? <ProblemBanner error={error} onRetry={onRetry} /> : null}
      {showEmpty ? (
        (empty ?? (
          <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
        ))
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface-raised">
          <Table>
            {caption ? <caption className="sr-only">{caption}</caption> : null}
            <TableHeader className="bg-surface-sunken">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    const label = header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext());
                    return (
                      <TableHead
                        key={header.id}
                        style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                        aria-sort={
                          sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : undefined
                        }
                      >
                        {canSort ? (
                          <button
                            type="button"
                            className="-ml-2 inline-flex h-8 items-center gap-1 rounded-md px-2 hover:bg-surface-sunken hover:text-fg"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {label}
                            {sorted === 'asc' ? (
                              <ArrowUp className="size-3.5" aria-hidden="true" />
                            ) : sorted === 'desc' ? (
                              <ArrowDown className="size-3.5" aria-hidden="true" />
                            ) : (
                              <ArrowUpDown className="size-3.5 opacity-40" aria-hidden="true" />
                            )}
                          </button>
                        ) : (
                          label
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading && data === undefined
                ? skeletonKeys(skeletonRows).map((rowKey) => (
                    <TableRow key={rowKey} aria-hidden="true">
                      {table.getAllLeafColumns().map((column) => (
                        <TableCell key={column.id} className={cellPad}>
                          <Skeleton className="h-4 w-full max-w-48" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() ? 'selected' : undefined}
                      className={cn(onRowClick && 'cursor-pointer')}
                      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className={cellPad}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </div>
      )}
      {footer || (hasMore && onLoadMore) ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-fg-muted text-sm">{footer}</div>
          {hasMore && onLoadMore ? (
            <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? <LoaderCircle className="animate-spin" /> : null}
              {loadMoreLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const EMPTY: never[] = [];

const skeletonKeys = (count: number): string[] =>
  Array.from({ length: count }, (_, n) => `skeleton-${n}`);

function selectionColumn<T>(): ColumnDef<T, unknown> {
  return {
    id: '__select',
    size: 36,
    enableSorting: false,
    header: ({ table }) => (
      <Checkbox
        aria-label="Select all rows"
        checked={
          table.getIsAllPageRowsSelected()
            ? true
            : table.getIsSomePageRowsSelected()
              ? 'indeterminate'
              : false
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(value === true)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label="Select row"
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(value === true)}
        onClick={(event) => event.stopPropagation()}
      />
    ),
  };
}

/** Shape of a `usePagedList()` result the table understands. */
export interface PagedSource<T> {
  items: T[];
  isPending: boolean;
  error: Error | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => unknown;
}

/** `<DataTable columns={cols} {...pagedTableProps(useUsers())} />` */
export function pagedTableProps<T>(
  source: PagedSource<T>,
): Pick<
  DataTableProps<T>,
  'data' | 'isLoading' | 'error' | 'onRetry' | 'hasMore' | 'onLoadMore' | 'isLoadingMore'
> {
  return {
    data: source.isPending ? undefined : source.items,
    isLoading: source.isPending,
    error: source.error,
    onRetry: () => void source.refetch(),
    hasMore: source.hasMore,
    onLoadMore: source.loadMore,
    isLoadingMore: source.isLoadingMore,
  };
}

/**
 * Converts table sorting to the search AST `sort` (server-side sorting). Column ids must be
 * field refs (`created_at`, `cf_…`).
 */
export function sortingToAst(sorting: SortingState): { field: string; dir: 'asc' | 'desc' }[] {
  return sorting.slice(0, 3).map((s) => ({ field: s.id, dir: s.desc ? 'desc' : 'asc' }));
}
